import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from 'reactstrap';
import { getPreviewText } from './templateUtils';

// Maps element type to FA icon class
const TYPE_ICONS = {
    text:    'fa-solid fa-font',
    image:   'fa-solid fa-image',
    qr:      'fa-solid fa-qrcode',
    barcode: 'fa-solid fa-barcode',
    shape:   'fa-solid fa-shapes',
};

const TYPE_LABEL_IDS = {
    text:    'TemplateDesigner.Palette.Text',
    image:   'TemplateDesigner.Palette.Image',
    qr:      'TemplateDesigner.Palette.QrCode',
    barcode: 'TemplateDesigner.Palette.Barcode',
    shape:   'TemplateDesigner.Palette.Shape',
};

const ELEMENT_TYPES = ['text', 'image', 'qr', 'barcode', 'shape'];

class ElementPalette extends Component {
    state = { dragOverLayerId: null };

    _onDragStart = (e, type) => {
        e.dataTransfer.setData('elementType', type);
        e.dataTransfer.effectAllowed = 'copy';
    };

    // Layer drag-and-drop reorder. Key must stay lower-case: browsers lower-case custom
    // dataTransfer types and types.includes() would silently fail.
    _onLayerDragStart = (e, id) => {
        e.dataTransfer.setData('layerid', id);
        e.dataTransfer.effectAllowed = 'move';
    };

    _onLayerDragOver = (e, id) => {
        if (!e.dataTransfer.types.includes('layerid')) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (this.state.dragOverLayerId !== id) this.setState({ dragOverLayerId: id });
    };

    _onLayerDrop = (e, targetId) => {
        const draggedId = e.dataTransfer.getData('layerid');
        this.setState({ dragOverLayerId: null });
        if (!draggedId) return;
        e.preventDefault();
        e.stopPropagation();
        this.props.onReorderLayer(draggedId, targetId);
    };

    _onLayerDragEnd = () => {
        this.setState({ dragOverLayerId: null });
    };

    _onChipClick = (type) => {
        this.props.onAddElement(type);
    };

    _layerIconStyle = (disabled = false) => ({
        border: 'none',
        background: 'transparent',
        padding: '4px 5px',
        fontSize: '0.75rem',
        color: disabled ? '#ced4da' : '#6c757d',
        cursor: disabled ? 'default' : 'pointer',
        lineHeight: 1,
    });

    render() {
        const { elements, selectedElementId, selectedElementIds = [], onSelectElement, onToggleLock, onDuplicateElement, onDeleteElement, onMoveLayer } = this.props;

        // Sort layers by zIndex descending for display
        const layersSorted = [...elements].sort((a, b) => b.zIndex - a.zIndex);

        return (
            <div style={{ width: 280, flexShrink: 0, borderRight: '1px solid #dee2e6', display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>
                {/* Element chips */}
                <div style={{ padding: '8px' }}>
                    {ELEMENT_TYPES.map(type => (
                        <div
                            key={type}
                            draggable="true"
                            onDragStart={(e) => this._onDragStart(e, type)}
                            onClick={() => this._onChipClick(type)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '6px 10px',
                                marginBottom: 4,
                                borderRadius: 4,
                                border: '1px solid #dee2e6',
                                cursor: 'grab',
                                backgroundColor: '#f8f9fa',
                                fontSize: '0.85rem',
                                userSelect: 'none',
                            }}
                            title="Drag to canvas or click to add"
                        >
                            <i className={TYPE_ICONS[type] + ' mr-2'} style={{ width: 16, textAlign: 'center' }} />
                            <FormattedMessage id={TYPE_LABEL_IDS[type]} />
                        </div>
                    ))}
                </div>

                {/* Layers list */}
                <div style={{ borderTop: '1px solid #dee2e6', padding: '8px', flexGrow: 1 }}>
                    <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#6c757d', marginBottom: 4 }}>
                        <FormattedMessage id="TemplateDesigner.Layers" />
                    </div>
                    {layersSorted.length === 0 && (
                        <div style={{ fontSize: '0.8rem', color: '#adb5bd', fontStyle: 'italic', padding: '4px 0' }}>
                            —
                        </div>
                    )}
                    {layersSorted.map((el, i) => (
                        <div
                            key={el.id}
                            onClick={(e) => onSelectElement(el.id, e.ctrlKey || e.metaKey || e.shiftKey)}
                            draggable="true"
                            onDragStart={(e) => this._onLayerDragStart(e, el.id)}
                            onDragOver={(e) => this._onLayerDragOver(e, el.id)}
                            onDrop={(e) => this._onLayerDrop(e, el.id)}
                            onDragEnd={this._onLayerDragEnd}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '7px 8px',
                                marginBottom: 3,
                                borderRadius: 4,
                                cursor: 'pointer',
                                backgroundColor: (selectedElementIds.includes(el.id) || selectedElementId === el.id) ? '#e8f0fe' : 'transparent',
                                border: (selectedElementIds.includes(el.id) || selectedElementId === el.id) ? '1px solid #007bff' : '1px solid transparent',
                                borderTop: this.state.dragOverLayerId === el.id
                                    ? '2px solid #007bff'
                                    : ((selectedElementIds.includes(el.id) || selectedElementId === el.id) ? '1px solid #007bff' : '1px solid transparent'),
                                fontSize: '0.82rem',
                            }}
                        >
                            <i className={TYPE_ICONS[el.type] + ' mr-2'} style={{ width: 14, textAlign: 'center', fontSize: '0.7rem', color: '#6c757d' }} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexGrow: 1 }}>
                                {el.type === 'text'
                                    ? (el.binding || getPreviewText(el.freeText) || 'Text')
                                    : el.type.charAt(0).toUpperCase() + el.type.slice(1)
                                }
                            </span>

                            {/* Per-layer actions; list is zIndex DESC, so "up" = +1 */}
                            <button
                                type="button"
                                title="Bring forward"
                                disabled={i === 0}
                                onClick={(e) => { e.stopPropagation(); if (i !== 0) onMoveLayer(el.id, +1); }}
                                style={this._layerIconStyle(i === 0)}
                            >
                                <i className="fa-solid fa-chevron-up" />
                            </button>
                            <button
                                type="button"
                                title="Send backward"
                                disabled={i === layersSorted.length - 1}
                                onClick={(e) => { e.stopPropagation(); if (i !== layersSorted.length - 1) onMoveLayer(el.id, -1); }}
                                style={this._layerIconStyle(i === layersSorted.length - 1)}
                            >
                                <i className="fa-solid fa-chevron-down" />
                            </button>
                            <button
                                type="button"
                                title={el.locked ? 'Unlock' : 'Lock'}
                                onClick={(e) => { e.stopPropagation(); onToggleLock(el.id); }}
                                style={{ ...this._layerIconStyle(), color: el.locked ? '#dc3545' : '#adb5bd' }}
                            >
                                <i className={el.locked ? 'fa-solid fa-lock' : 'fa-solid fa-lock-open'} />
                            </button>
                            <button
                                type="button"
                                title="Duplicate"
                                onClick={(e) => { e.stopPropagation(); onDuplicateElement(el.id); }}
                                style={this._layerIconStyle()}
                            >
                                <i className="fa-regular fa-copy" />
                            </button>
                            <button
                                type="button"
                                title={el.locked ? 'Locked' : 'Delete'}
                                disabled={!!el.locked}
                                onClick={(e) => { e.stopPropagation(); if (!el.locked) onDeleteElement(el.id); }}
                                style={this._layerIconStyle(!!el.locked)}
                            >
                                <i className="fa-regular fa-trash-can" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
}

export default ElementPalette;
